/**
 * 买家端偏好上下文：语言（zh/en/ko）为唯一开关，币种跟随语言派生。
 * - zh → CNY（¥）；en / ko → USD（$）。全站统一，无独立币种切换。
 * - USD 为展示转换，采用静态演示汇率，真实汇率接口接入后替换 DEMO_USD_RATE 即可
 *   （仅展示用，不伪造后端返回）。
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { isLang, setCurrentLang, translate, uiLocale, type Lang, type TrVars } from '@/i18n';
import { get } from '@/api/request';

export type Currency = 'CNY' | 'USD';

/** 演示用静态汇率（CNY→USD）兜底：每日真实汇率接口不可用时使用，避免展示崩坏。 */
export const DEMO_USD_RATE = 0.14;

interface PrefsValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: TrVars) => string;
  /** 币种跟随语言派生：zh=CNY / en、ko=USD（只读，无独立切换） */
  currency: Currency;
  /** 当前币种符号 */
  symbol: string;
  /** 将后端 CNY 金额按偏好币种转换为展示值 */
  toDisplay: (cny: number) => { value: number; symbol: string };
}

const KEY_LANG = 'bp_buyer_lang';
const KEY_CCY_LEGACY = 'bp_buyer_currency';

const PrefsContext = createContext<PrefsValue | undefined>(undefined);

function readStoredLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY_LANG);
    return isLang(saved) ? saved : 'zh';
  } catch {
    return 'zh';
  }
}

export function BuyerPrefsProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang);
  // 渲染期同步当前语言，保证子组件/工具函数里的 tr() 与本次渲染一致
  setCurrentLang(lang);

  // 每日真实汇率（CNY→USD）：启动拉取一次，失败/无数据时回退演示汇率
  const [usdRate, setUsdRate] = useState<number>(DEMO_USD_RATE);
  useEffect(() => {
    let cancelled = false;
    get<number>('/buyer/price/usd-rate')
      .then((v) => { if (!cancelled && v != null && v > 0) setUsdRate(v); })
      .catch(() => { /* 静默回退演示汇率 */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(KEY_LANG, lang); } catch { /* ignore */ }
    document.documentElement.lang = uiLocale(lang);
  }, [lang]);
  // 旧版独立币种键已废弃（币种跟随语言），清理历史残留
  useEffect(() => { try { localStorage.removeItem(KEY_CCY_LEGACY); } catch { /* ignore */ } }, []);

  const setLang = useCallback((l: Lang) => setLangState(l), []);

  const t = useCallback((key: string, vars?: TrVars) => translate(lang, key, vars), [lang]);

  const currency: Currency = lang === 'zh' ? 'CNY' : 'USD';

  const toDisplay = useCallback((cny: number) => {
    if (currency === 'USD') return { value: +(cny * usdRate).toFixed(2), symbol: '$' };
    return { value: +cny.toFixed(2), symbol: '¥' };
  }, [currency, usdRate]);

  return (
    <PrefsContext.Provider
      value={{
        lang,
        setLang,
        t,
        currency,
        symbol: currency === 'USD' ? '$' : '¥',
        toDisplay,
      }}
    >
      {children}
    </PrefsContext.Provider>
  );
}

export function usePrefs(): PrefsValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs 必须在 BuyerPrefsProvider 内部使用');
  return ctx;
}
